import { useMemo, useRef, useEffect, useCallback, useState } from 'react'

import { Modal, Spin } from 'antd'
import { useFormik } from 'formik'

import { Button } from 'common/components/Button/Button'
import { BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { SignatureDialog } from 'common/components/SignatureDialog'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  useFetchDocTemplateQuery,
  useSubmitDocTemplateMutation,
} from 'features/Procedure/state/procedure.api'

import {
  flattenValues,
  extractFormFields,
  getRequiredFieldNames,
  getRequiredFieldValues,
  setNestedValue,
  getTemplateValidationError,
  injectFormValues,
} from './utils'
import { buildBaseHtml, mapSubmitDocTemplateErrorMessage } from './dialog-helpers'
import styles from './doc-template-preview-dialog.module.scss'

type Props = {
  isVisible: boolean
  onClose: () => void
  patientId: number
  encounterUid: string
  templateId: number
  title?: string
  onSubmitSuccess?: () => void
}

type SignatureCanvasSize = {
  width: number
  height: number
}

const DEFAULT_SIGNATURE_CANVAS_SIZE: SignatureCanvasSize = { width: 440, height: 200 }

export const DocTemplatePreviewDialog = ({
  isVisible,
  onClose,
  patientId,
  encounterUid,
  templateId,
  title = 'Document Preview',
  onSubmitSuccess,
}: Props) => {
  const {
    data: response,
    isLoading,
    isFetching,
  } = useFetchDocTemplateQuery(
    { patientId, encounterUid, templateId },
    { skip: !isVisible || !patientId || !encounterUid || !templateId },
  )

  const [submitDocTemplate, { isLoading: isSubmitting }] = useSubmitDocTemplateMutation()
  const { setNotification } = useNotification()

  const isLoadingContent = isLoading || isFetching
  const templateData = response?.data
  const templateContent = templateData?.template_content || ''
  const templateFieldData = templateData?.template_data
  const documentTitle =
    templateData?.file_name || (typeof title === 'string' ? title : 'Document Preview')

  const baseHtml = useMemo(() => {
    return buildBaseHtml(templateContent, templateFieldData)
  }, [templateContent, templateFieldData])

  const initialValues = useMemo(() => {
    if (!baseHtml) return {}
    return extractFormFields(baseHtml)
  }, [baseHtml])

  const {
    patientSignatureFieldName,
    physicianSignatureFieldName,
    witnessNameFieldName,
    witnessSignatureFieldName,
  } = useMemo(() => getRequiredFieldNames(baseHtml), [baseHtml])

  const formik = useFormik<Record<string, any>>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const flat = flattenValues(values)
      const templateDataPayload = Object.entries(flat).map(([name, value]) => ({
        name,
        value,
      }))

      const validationError = getTemplateValidationError(
        values,
        {
          patientSignatureFieldName,
          physicianSignatureFieldName,
          witnessNameFieldName,
          witnessSignatureFieldName,
        },
        formik.initialValues,
      )
      if (validationError) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: validationError,
        })
        return
      }

      const { patientSignature } = getRequiredFieldValues(values, {
        patientSignatureFieldName,
        physicianSignatureFieldName,
        witnessNameFieldName,
        witnessSignatureFieldName,
      })

      try {
        await submitDocTemplate({
          patientId,
          templateId,
          payload: {
            patientSignature,
            templateData: JSON.stringify(templateDataPayload),
          },
        }).unwrap()

        formik.resetForm()
        onSubmitSuccess?.()
        onClose()
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: mapSubmitDocTemplateErrorMessage(error?.data?.message),
        })
      }
    },
  })

  const htmlContent = useMemo(() => {
    if (!baseHtml) return ''
    return injectFormValues(baseHtml, { ...formik.initialValues, ...formik.values })
  }, [baseHtml, formik.initialValues, formik.values])

  const [signatureFieldName, setSignatureFieldName] = useState<string | null>(null)
  const [signatureCanvasSize, setSignatureCanvasSize] = useState<SignatureCanvasSize>(
    DEFAULT_SIGNATURE_CANVAS_SIZE,
  )

  const handleOpenSignatureDialog = useCallback((fieldName: string, canvasRatio?: number) => {
    if (canvasRatio && Number.isFinite(canvasRatio) && canvasRatio > 0) {
      const width = DEFAULT_SIGNATURE_CANVAS_SIZE.width
      const height = Math.max(90, Math.min(260, Math.round(width / canvasRatio)))
      setSignatureCanvasSize({ width, height })
    } else {
      setSignatureCanvasSize(DEFAULT_SIGNATURE_CANVAS_SIZE)
    }

    setSignatureFieldName(fieldName)
  }, [])

  const handleCloseSignatureDialog = useCallback(() => {
    setSignatureFieldName(null)
    setSignatureCanvasSize(DEFAULT_SIGNATURE_CANVAS_SIZE)
  }, [])

  const handleSignatureComplete = useCallback(
    (signatureDataUrl: string) => {
      if (signatureFieldName) {
        formik.setValues((prevValues) =>
          setNestedValue(prevValues, signatureFieldName, signatureDataUrl),
        )
      }
    },
    [signatureFieldName, formik.setValues],
  )

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisible || isLoadingContent || !baseHtml) return

    const container = contentRef.current
    if (!container) return

    const getSignatureImageTarget = (target: HTMLElement): HTMLImageElement | null => {
      const imgTarget =
        target instanceof HTMLImageElement
          ? target
          : (target.closest('img') as HTMLImageElement | null)
      if (!(imgTarget instanceof HTMLImageElement)) return null

      const dataType = (imgTarget.getAttribute('data-type') || '').toLowerCase()
      const imageId = (imgTarget.getAttribute('id') || '').toLowerCase()
      const imageName = (imgTarget.getAttribute('name') || '').toLowerCase()
      const isSignatureImage =
        imgTarget.classList.contains('signature') ||
        dataType.includes('signature') ||
        imgTarget.getAttribute('data-action') === 'fetch_signature' ||
        imageId.includes('signature') ||
        imageName.includes('signature')

      return isSignatureImage ? imgTarget : null
    }

    const signatureImages = container.querySelectorAll<HTMLImageElement>('img')
    signatureImages.forEach((img) => {
      if (!getSignatureImageTarget(img)) return
      img.draggable = false
      img.setAttribute('draggable', 'false')
    })

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const imgTarget = getSignatureImageTarget(target)
      if (!imgTarget) return

      e.preventDefault()
      const imageName = imgTarget.getAttribute('name')
      const imageFieldKey = imageName || imgTarget.getAttribute('id')
      const imageId = (imgTarget.getAttribute('id') || '').toLowerCase()
      const hasAdminOrPhysicianSignature =
        (imageId.includes('admin') ||
          imageId.includes('physician') ||
          imageId.includes('doctor')) &&
        imageId.includes('signature')
      const name =
        imageFieldKey ||
        (imageId.includes('witness') && imageId.includes('signature')
          ? witnessSignatureFieldName
          : null) ||
        (hasAdminOrPhysicianSignature ? physicianSignatureFieldName : null) ||
        (imageId.includes('patient') && imageId.includes('signature')
          ? patientSignatureFieldName
          : null) ||
        (imgTarget.id === 'patientSignature' ? patientSignatureFieldName : null)

      if (name) {
        const rect = imgTarget.getBoundingClientRect()
        const ratio = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : undefined
        handleOpenSignatureDialog(name, ratio)
      }
    }

    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement
      const imgTarget = getSignatureImageTarget(target)
      if (!imgTarget) return
      e.preventDefault()
    }

    const handleChange = (e: Event) => {
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        const fieldName = target.getAttribute('name') || target.getAttribute('id')
        if (!fieldName) return

        if (target instanceof HTMLInputElement && target.type === 'checkbox') {
          formik.setValues((prevValues) =>
            setNestedValue(prevValues, fieldName, target.checked ? 'on' : ''),
          )
        } else {
          formik.setValues((prevValues) => setNestedValue(prevValues, fieldName, target.value))
        }
      }
    }

    container.addEventListener('click', handleClick)
    container.addEventListener('dragstart', handleDragStart)
    container.addEventListener('change', handleChange)

    return () => {
      container.removeEventListener('click', handleClick)
      container.removeEventListener('dragstart', handleDragStart)
      container.removeEventListener('change', handleChange)
    }
  }, [
    isVisible,
    isLoadingContent,
    baseHtml,
    handleOpenSignatureDialog,
    formik.setValues,
    patientSignatureFieldName,
    physicianSignatureFieldName,
    witnessSignatureFieldName,
  ])

  const handleClose = useCallback(() => {
    formik.resetForm()
    onClose()
  }, [formik, onClose])

  return (
    <>
      <Modal
        classNames={{
          content: styles.modalContent,
          wrapper: styles.modalWrapper,
        }}
        closeIcon={null}
        open={isVisible}
        onCancel={handleClose}
        maskClosable={false}
        keyboard={false}
        title={null}
        destroyOnHidden
        footer={null}
        style={{ top: 20 }}>
        <div className={styles.container}>
          <h2 className={styles.dialogTitle}>{documentTitle}</h2>
          <div className={styles.contentWrapper}>
            {isLoadingContent ? (
              <div className={styles.loadingWrapper}>
                <Spin size='large' />
              </div>
            ) : (
              <div
                ref={contentRef}
                className={styles.openEmrContainer}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            )}
          </div>
          {!isLoadingContent && (
            <div className={styles.actions}>
              <Button severity={BUTTON_SEVERITY.DANGER} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => formik.handleSubmit()}
                loading={isSubmitting}
                disabled={isSubmitting}>
                Submit
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <SignatureDialog
        isVisible={Boolean(signatureFieldName)}
        onClose={handleCloseSignatureDialog}
        onSign={handleSignatureComplete}
        title='Draw Your Signature'
        canvasWidth={signatureCanvasSize.width}
        canvasHeight={signatureCanvasSize.height}
      />
    </>
  )
}
